import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment as env } from "../../../../environments/environment";
import { CommonService } from "../../core/core/services/common.service";
import { LoginRequestModel, LoginResponseModel } from "../models/login.model";
import { RegisterModel } from "../models/register.model";
import {
  PasswordRecoveryRequestModel,
  PasswordRecoveryResponseModel,
  PasswordResetRequestModel,
  PasswordResetResponseModel
} from "../models/password-recovery.model";
import * as jwtDecodeModule from "jwt-decode";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  /** Contraseña temporal almacenada en memoria (nunca en storage) */
  private _tempCurrentPassword: string | null = null;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly commonService: CommonService
  ) {}

  login(req: LoginRequestModel): Observable<LoginResponseModel> {
    this.commonService.removeToken();

    return this.httpClient
      .post<LoginResponseModel>(
        `${env.url_ms_base}${env.login_conf.DOMAIN_ROUTE}${env.login_conf.LOGIN_ENDPOINT}`,
        req
      )
      .pipe();
  }

  register(req: RegisterModel): Observable<boolean> {
    return this.httpClient
      .post<boolean>(
        `${env.url_ms_base}${env.login_conf.DOMAIN_ROUTE}${env.login_conf.REGISTER_ENDPOINT}`,
        req
      )
      .pipe();
  }

  registerLoginInfo(loginValue: LoginResponseModel) {
    Object.entries(loginValue).forEach(([key, value]) => {
      sessionStorage.setItem(key, value);
    });
  }

  /**
   * Verifica si el usuario requiere cambio de contraseña forzado
   *
   * @returns true si el cambio de contraseña es obligatorio
   */
  isPasswordChangeRequired(): boolean {
    return sessionStorage.getItem('passwordChangeRequired') === 'true';
  }

  /**
   * Limpia el flag de cambio de contraseña requerido
   */
  clearPasswordChangeRequired(): void {
    sessionStorage.removeItem('passwordChangeRequired');
  }

  /**
   * Almacena temporalmente la contraseña actual solo en memoria.
   * No se persiste en sessionStorage/localStorage para evitar exposición.
   * Se pierde al navegar fuera o refrescar (flujo de re-login requerido).
   */
  setTempCurrentPassword(plainPassword: string): void {
    this._tempCurrentPassword = plainPassword;
  }

  /**
   * Recupera la contraseña temporal actual desde memoria.
   */
  getTempCurrentPassword(): string | null {
    return this._tempCurrentPassword;
  }

  /**
   * Elimina la contraseña temporal de memoria.
   * Debe llamarse después del cambio exitoso de contraseña.
   */
  clearTempCurrentPassword(): void {
    this._tempCurrentPassword = null;
  }

  /**
   * Verifica si existe una contraseña temporal en memoria.
   */
  hasTempCurrentPassword(): boolean {
    return this._tempCurrentPassword !== null;
  }

  /**
   * Decodifica un token JWT para obtener su payload
   *
   * @param token - Token JWT a decodificar
   * @returns Payload del token o objeto vacío si falla
   *
   * @remarks
   * Soporta múltiples formas de importar jwt-decode:
   * - globalThis.jwtDecode (SSR compatible)
   * - jwtDecodeModule.jwtDecode (CommonJS)
   * Retorna {} vacío si no se puede decodificar
   */
  getDecodedToken(token: string): Record<string, unknown> {
    try {
      const jwtDecodeFn =
        (globalThis as unknown as { jwtDecode?: unknown }).jwtDecode ||
        (jwtDecodeModule && (jwtDecodeModule as unknown as { jwtDecode: (token: string) => Record<string, unknown> }).jwtDecode);

      return jwtDecodeFn ? (jwtDecodeFn as (token: string) => Record<string, unknown>)(token) : {};
    } catch (error) {
      console.error("Error decodificando el token:", error);
      return {};
    }
  }

  requestPasswordRecovery(email: string): Observable<PasswordRecoveryResponseModel> {
    const body: PasswordRecoveryRequestModel = { email };
    return this.httpClient
      .post<PasswordRecoveryResponseModel>(
        `${env.url_ms_base}${env.login_conf.DOMAIN_ROUTE}${env.login_conf.PASSWORD_RECOVERY_REQUEST}`,
        body
      );
  }

  resetPassword(token: string, newPassword: string, confirmPassword: string): Observable<PasswordResetResponseModel> {
    const body: PasswordResetRequestModel = { token, newPassword, confirmPassword };
    return this.httpClient
      .post<PasswordResetResponseModel>(
        `${env.url_ms_base}${env.login_conf.DOMAIN_ROUTE}${env.login_conf.PASSWORD_RECOVERY_RESET}`,
        body
      );
  }
}
